import { useEffect, useMemo, useRef } from 'react';
import { View, StyleSheet, Animated, Pressable, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { ThemedText } from './Text';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export type ToastAction = {
  label: string;
  onPress: () => void;
  accessibilityLabel?: string;
};

export type Toast = {
  id: string;
  message: string;
  details?: string;
  type?: ToastType;
  duration?: number;
  actions?: ToastAction[];
  onUndo?: () => void;
};

type ToastProps = {
  toast: Toast;
  onDismiss: (id: string) => void;
};

export function ToastComponent({ toast, onDismiss }: ToastProps) {
  const theme = useTheme();
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(-100)).current;

  useEffect(() => {
    // Animate in
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 300,
        useNativeDriver: true,
      }),
      Animated.spring(slideAnim, {
        toValue: 0,
        useNativeDriver: true,
        tension: 65,
        friction: 11,
      }),
    ]).start();

    // Auto-dismiss after duration (duration=0 disables auto-dismiss)
    if (toast.duration === 0) return;
    const timer = setTimeout(() => {
      handleDismiss();
    }, toast.duration ?? 3000);

    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = () => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 0,
        duration: 200,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: -100,
        duration: 200,
        useNativeDriver: true,
      }),
    ]).start(() => {
      onDismiss(toast.id);
    });
  };

  const getToastStyles = () => {
    const type = toast.type || 'info';
    switch (type) {
      case 'success':
        return {
          backgroundColor: theme.colors.success,
          icon: 'checkmark-circle' as const,
        };
      case 'error':
        return {
          backgroundColor: theme.colors.errorContainer,
          icon: 'close-circle' as const,
          iconColor: theme.colors.error,
          textColor: theme.colors.onErrorContainer,
        };
      case 'warning':
        return {
          backgroundColor: theme.colors.warningContainer,
          icon: 'warning' as const,
          iconColor: theme.colors.warning,
          textColor: theme.colors.onWarningContainer,
        };
      default:
        return {
          backgroundColor: theme.colors.primary,
          icon: 'information-circle' as const,
          iconColor: undefined,
          textColor: undefined,
        };
    }
  };

  const { backgroundColor, icon, iconColor, textColor } = getToastStyles();
  const finalIconColor = iconColor ?? theme.colors.textInverse;
  const finalTextColor = textColor ?? theme.colors.textInverse;

  const actions = useMemo(() => {
    const base = toast.actions ?? [];
    if (!toast.onUndo) return base;
    return [
      ...base,
      {
        label: 'Undo',
        onPress: () => {
          toast.onUndo?.();
          handleDismiss();
        },
        accessibilityLabel: 'Undo last change',
      },
    ];
  }, [toast.actions, toast.onUndo]);

  return (
    <Animated.View
      style={[
        styles.container,
        {
          opacity: fadeAnim,
          transform: [{ translateY: slideAnim }],
        },
      ]}
    >
      <View
        style={[
          styles.toast,
          { backgroundColor },
          Platform.OS === 'web' && {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          },
        ]}
        accessibilityRole="alert"
        accessibilityLabel={`${toast.type || 'info'} notification`}
      >
        <Ionicons name={icon} size={20} color={finalIconColor} />
        <View style={styles.content}>
        <ThemedText
          variant="body"
          style={[styles.message, { color: finalTextColor }]}
          numberOfLines={2}
        >
          {toast.message}
        </ThemedText>
          {!!toast.details && (
            <ThemedText
              variant="caption"
              style={[styles.details, { color: finalTextColor }]}
              numberOfLines={2}
            >
              {toast.details}
            </ThemedText>
          )}
          {actions.length > 0 && (
            <View style={styles.actionsRow}>
              {actions.map((action, idx) => (
                <Pressable
                  key={`${toast.id}-action-${idx}`}
                  onPress={() => {
                    action.onPress();
                  }}
                  style={({ pressed }) => [
                    styles.actionButton,
                    { borderColor: finalTextColor, opacity: pressed ? 0.75 : 1 },
                  ]}
                  accessibilityRole="button"
                  accessibilityLabel={action.accessibilityLabel ?? action.label}
                >
                  <ThemedText variant="caption" style={[styles.actionText, { color: finalTextColor }]}>
                    {action.label}
                  </ThemedText>
                </Pressable>
              ))}
            </View>
          )}
        </View>
        <Pressable
          onPress={handleDismiss}
          style={styles.dismissButton}
          accessibilityRole="button"
          accessibilityLabel="Dismiss notification"
        >
          <Ionicons name="close" size={18} color={finalIconColor} />
        </Pressable>
      </View>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginBottom: 8,
  },
  toast: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    minHeight: 44, // Accessibility: minimum touch target
    ...(Platform.OS !== 'web' && {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.25,
      shadowRadius: 4,
      elevation: 5,
    }),
  },
  content: {
    flex: 1,
    marginLeft: 12,
  },
  message: {
    fontSize: 14,
  },
  details: {
    marginTop: 2,
    fontSize: 12,
    opacity: 0.9,
  },
  actionsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 8,
  },
  actionButton: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderWidth: 1,
    borderRadius: 999,
  },
  actionText: {
    fontSize: 12,
  },
  dismissButton: {
    marginLeft: 8,
    padding: 4,
    minWidth: 32,
    minHeight: 32,
    justifyContent: 'center',
    alignItems: 'center',
  },
});

